import EventProcessor from "./internal/event/EventProcessor"
import {
  Decision,
  Experiment,
  FeatureFlagDecision,
  HackleEvent,
  HackleUser,
  MatchValueType,
  RemoteConfigDecision
} from "./internal/model/model"
import UserEvent from "./internal/event/UserEvent"
import Logger from "./internal/logger"
import WorkspaceConfigFetcher from "./internal/workspace/config/WorkspaceConfigFetcher"
import ObjectUtil from "./internal/util/ObjectUtil"
import TraceTransformer from "./internal/trace/TraceTransformer"
import { Comparable } from "./internal/util/Comparable"
import RemoteConfigLocalEvaluator from "./internal/evaluation/service/remoteconfig/mode/local/RemoteConfigLocalEvaluator"
import ExperimentLocalEvaluator from "./internal/evaluation/service/experiment/mode/local/ExperimentLocalEvaluator"
import ExperimentRemoteEvaluator from "./internal/evaluation/service/experiment/mode/remote/ExperimentRemoteEvaluator"
import RemoteConfigRemoteEvaluator from "./internal/evaluation/service/remoteconfig/mode/remote/RemoteConfigRemoteEvaluator"
import DecisionProcessor from "./internal/decision/DecisionProcessor"
import LocalDecisionProcessor from "./internal/decision/LocalDecisionProcessor"
import RemoteDecisionProcessor from "./internal/decision/RemoteDecisionProcessor"
import WorkspaceEvaluationFetcher from "./internal/workspace/evaluation/WorkspaceEvaluationFetcher"
import { EvaluationMode } from "../public/model"
import {
  DelegatingManualOverrideStorage,
  ManualOverrideStorage
} from "./internal/evaluation/service/experiment/match/ManualOverrideStorage"
import EvaluatorFactory from "./internal/evaluation/evaluator/EvaluatorFactory"
import { SystemClock } from "./internal/util/TimeUtil"
import { HackleCoreContext } from "./internal/HackleCoreContext"
import RemoteConfigParameterTargetRuleDeterminer from "./internal/evaluation/service/remoteconfig/match/RemoteConfigParameterTargetRuleDeterminer"
import ExperimentLocalEvaluationFlowFactory from "./internal/evaluation/service/experiment/flow/ExperimentLocalEvaluationFlowFactory"
import TargetMatcher from "./internal/evaluation/match/TargetMatcher"
import { ConditionMatcherFactory } from "./internal/evaluation/match/ConditionMatcher"
import Bucketer from "./internal/evaluation/bucket/Bucketer"
import EvaluateProcessor from "./internal/evaluation/EvaluateProcessor"
import EvaluationEventFactory from "./internal/evaluation/event/EvaluationEventFactory"
import EvaluationEventRecorder from "./internal/evaluation/event/EvaluationEventRecorder"
import ExperimentReferenceLocalEvaluator from "./internal/evaluation/service/experiment/mode/local/ExperimentReferenceLocalEvaluator"
import { InAppMessageLayoutSelector } from "./internal/evaluation/service/inappmessage/layout/match/InAppMessageLayoutSelector"
import { InAppMessageLayoutLocalEvaluator } from "./internal/evaluation/service/inappmessage/layout/mode/local/InAppMessageLayoutLocalEvaluator"
import { InAppMessageLayoutRemoteEvaluator } from "./internal/evaluation/service/inappmessage/layout/mode/remote/InAppMessageLayoutRemoteEvaluator"
import InAppMessageEligibilityLocalEvaluationFlowFactory from "./internal/evaluation/service/inappmessage/eligibility/flow/InAppMessageEligibilityLocalEvaluationFlowFactory"
import InAppMessageEligibilityRemoteEvaluationFlowFactory from "./internal/evaluation/service/inappmessage/eligibility/flow/InAppMessageEligibilityRemoteEvaluationFlowFactory"
import { InAppMessageEligibilityLocalEvaluator } from "./internal/evaluation/service/inappmessage/eligibility/mode/local/InAppMessageEligibilityLocalEvaluator"
import { InAppMessageEligibilityRemoteEvaluator } from "./internal/evaluation/service/inappmessage/eligibility/mode/remote/InAppMessageEligibilityRemoteEvaluator"
import InAppMessageHiddenStorage from "./internal/evaluation/service/inappmessage/eligibility/match/InAppMessageHiddenStorage"
import { InAppMessageImpressionStorage } from "./internal/evaluation/service/inappmessage/eligibility/match/InAppMessageImpressionStorage"
import WorkspaceFetcher from "./internal/workspace/WorkspaceFetcher"

const log = Logger.log

/**
 * The mode-specific collaborators assembled before the single HackleCore instantiation.
 * Each evaluation mode registers its evaluators into the shared factory and returns this shape,
 * so `create` constructs HackleCore exactly once regardless of mode.
 */
interface HackleCoreComponents {
  decisionProcessor: DecisionProcessor
  workspaceFetcher: WorkspaceFetcher
  evaluateProcessor: EvaluateProcessor
}

/**
 * DO NOT use this class directly.
 * This class is only used internally by Hackle.
 * Backward compatibility is not supported.
 * Please use server-side or client-side HackleClient instead.
 */
export default class HackleCore {
  constructor(
    private readonly decisionProcessor: DecisionProcessor,
    private readonly workspaceFetcher: WorkspaceFetcher,
    private readonly eventProcessor: EventProcessor,
    private readonly errorDedupDeterminer: ErrorDedupDeterminer,
    readonly evaluateProcessor: EvaluateProcessor
  ) {}

  experiment(experimentKey: number, user: HackleUser): Decision {
    return this.decisionProcessor.experiment(experimentKey, user)
  }

  experiments(user: HackleUser): Comparable<Experiment, Decision> {
    return this.decisionProcessor.experiments(user)
  }

  featureFlag(featureKey: number, user: HackleUser): FeatureFlagDecision {
    return this.decisionProcessor.featureFlag(featureKey, user)
  }

  featureFlags(user: HackleUser): Comparable<Experiment, FeatureFlagDecision> {
    return this.decisionProcessor.featureFlags(user)
  }

  track(event: HackleEvent, user: HackleUser, timestamp: number = new Date().getTime()) {
    if (!event) {
      log.warn("event must not be null.")
      return
    }

    if (typeof event !== "object") {
      log.warn("Event must be event type.")
      return
    }

    if (typeof event === "object") {
      if (!event.key || typeof event.key !== "string") {
        log.warn("Event key must be not null. or event key must be string type.")
        return
      }
    }

    const workspace = this.workspaceFetcher.workspace(user)
    this.eventProcessor.process(UserEvent.track(timestamp, user, workspace, event))
  }

  trackException(exception: Error, user: HackleUser) {
    if (this.errorDedupDeterminer.isDedupTarget(exception)) {
      return
    }

    const event = TraceTransformer.toEvent(exception)

    this.flush()
    this.track(event, user)
    this.flush()
  }

  flush(sync: boolean = false) {
    this.eventProcessor.flush(sync)
  }

  remoteConfig(
    parameterKey: string,
    user: HackleUser,
    requiredType: MatchValueType,
    defaultValue: string | number | boolean
  ): RemoteConfigDecision {
    return this.decisionProcessor.remoteConfig(parameterKey, user, requiredType, defaultValue)
  }

  close(): void {
    this.eventProcessor.close()
  }

  // Owns the evaluation substrate: the event recorder and the active mode's evaluator
  // registrations (experiment/remote config/in-app message) are created here. Only the active
  // mode is registered because the evaluation mode is fixed after bootstrap.
  static create(
    workspaceFetcher: WorkspaceConfigFetcher,
    eventProcessor: EventProcessor,
    manualOverrideStorages: ManualOverrideStorage[],
    inAppMessageImpressionStorage: InAppMessageImpressionStorage,
    inAppMessageHiddenStorage: InAppMessageHiddenStorage,
    mode: EvaluationMode = "local",
    workspaceEvaluationFetcher: WorkspaceEvaluationFetcher | null = null
  ): HackleCore {
    const clock = SystemClock.instance
    const evaluatorFactory = HackleCoreContext.register("evaluatorFactory", new EvaluatorFactory())
    const bucketer = new Bucketer()
    const targetMatcher = HackleCoreContext.register(
      "targetMatcher",
      new TargetMatcher(new ConditionMatcherFactory(evaluatorFactory, clock))
    )
    const eventRecorder = new EvaluationEventRecorder(new EvaluationEventFactory(clock), eventProcessor)

    const components =
      mode === "remote"
        ? HackleCore.remoteComponents(
            evaluatorFactory,
            eventRecorder,
            inAppMessageImpressionStorage,
            inAppMessageHiddenStorage,
            workspaceEvaluationFetcher
          )
        : HackleCore.localComponents(
            evaluatorFactory,
            eventRecorder,
            bucketer,
            targetMatcher,
            new DelegatingManualOverrideStorage(manualOverrideStorages),
            workspaceFetcher,
            inAppMessageImpressionStorage,
            inAppMessageHiddenStorage
          )

    return new HackleCore(
      components.decisionProcessor,
      components.workspaceFetcher,
      eventProcessor,
      new ErrorDedupDeterminer(),
      components.evaluateProcessor
    )
  }

  private static remoteComponents(
    evaluatorFactory: EvaluatorFactory,
    eventRecorder: EvaluationEventRecorder,
    inAppMessageImpressionStorage: InAppMessageImpressionStorage,
    inAppMessageHiddenStorage: InAppMessageHiddenStorage,
    workspaceEvaluationFetcher: WorkspaceEvaluationFetcher | null
  ): HackleCoreComponents {
    const remoteFetcher = ObjectUtil.requiredNotNullOrUndefined(
      workspaceEvaluationFetcher,
      () => "workspaceEvaluationFetcher is required in remote evaluation mode"
    )
    evaluatorFactory.add(new ExperimentRemoteEvaluator(eventRecorder))
    evaluatorFactory.add(new RemoteConfigRemoteEvaluator(eventRecorder))

    const layoutEvaluator = new InAppMessageLayoutRemoteEvaluator(eventRecorder)
    const eligibilityFlowFactory = new InAppMessageEligibilityRemoteEvaluationFlowFactory(
      inAppMessageImpressionStorage,
      inAppMessageHiddenStorage,
      layoutEvaluator
    )
    evaluatorFactory.add(layoutEvaluator)
    evaluatorFactory.add(new InAppMessageEligibilityRemoteEvaluator(eligibilityFlowFactory, eventRecorder))

    const evaluateProcessor = new EvaluateProcessor(evaluatorFactory)
    // Remote mode does not load the on-device workspace config. The server-resolved per-user
    // evaluation is the workspace surface used for event enrichment; config introspection
    return {
      decisionProcessor: new RemoteDecisionProcessor(remoteFetcher, evaluateProcessor),
      workspaceFetcher: remoteFetcher,
      evaluateProcessor
    }
  }

  private static localComponents(
    evaluatorFactory: EvaluatorFactory,
    eventRecorder: EvaluationEventRecorder,
    bucketer: Bucketer,
    targetMatcher: TargetMatcher,
    manualOverrideStorage: ManualOverrideStorage,
    workspaceFetcher: WorkspaceConfigFetcher,
    inAppMessageImpressionStorage: InAppMessageImpressionStorage,
    inAppMessageHiddenStorage: InAppMessageHiddenStorage
  ): HackleCoreComponents {
    const experimentFlowFactory = new ExperimentLocalEvaluationFlowFactory(
      manualOverrideStorage,
      bucketer,
      targetMatcher
    )
    evaluatorFactory.add(new ExperimentLocalEvaluator(experimentFlowFactory, eventRecorder))
    evaluatorFactory.add(
      new RemoteConfigLocalEvaluator(
        new RemoteConfigParameterTargetRuleDeterminer(targetMatcher, bucketer),
        eventRecorder
      )
    )

    const layoutEvaluator = new InAppMessageLayoutLocalEvaluator(
      new ExperimentReferenceLocalEvaluator(evaluatorFactory),
      new InAppMessageLayoutSelector(),
      eventRecorder
    )
    const eligibilityFlowFactory = new InAppMessageEligibilityLocalEvaluationFlowFactory(
      targetMatcher,
      inAppMessageImpressionStorage,
      inAppMessageHiddenStorage,
      layoutEvaluator
    )
    evaluatorFactory.add(layoutEvaluator)
    evaluatorFactory.add(new InAppMessageEligibilityLocalEvaluator(eligibilityFlowFactory, eventRecorder))

    const evaluateProcessor = new EvaluateProcessor(evaluatorFactory)
    return {
      decisionProcessor: new LocalDecisionProcessor(workspaceFetcher, evaluateProcessor),
      workspaceFetcher,
      evaluateProcessor
    }
  }
}

export class ErrorDedupDeterminer {
  private previous?: Error

  isDedupTarget(error: Error): boolean {
    if (this._isSameError(error, this.previous)) {
      return true
    }
    this.previous = error
    return false
  }

  _isSameError(currentError: Error, previousError?: Error): boolean {
    if (ObjectUtil.isNullOrUndefined(previousError)) {
      return false
    }
    return (
      currentError.name === previousError.name &&
      currentError.message === previousError.message &&
      currentError.stack === previousError.stack
    )
  }
}
